import { Layers } from 'lucide-react';

interface TakeItFurtherContent {
  actionType: 'collect_only' | 'micro_action' | 'custom';
  customText?: string;
  microActionId?: string;
}

interface TakeItFurtherEditorEnhancedProps {
  content: TakeItFurtherContent;
  onChange: (content: TakeItFurtherContent) => void;
  disabled?: boolean;
  civicLevel?: string;
}

export default function TakeItFurtherEditorEnhanced({
  content,
  onChange,
  disabled = false,
  civicLevel = '',
}: TakeItFurtherEditorEnhancedProps) {
  const isObserver = civicLevel.toLowerCase() === 'observer';

  // Observer is always collect-only (locked)
  if (isObserver) {
    return (
      <div className="space-y-5">
        <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-5 h-5 rounded-full bg-[#40E0D0]/20 border border-[#40E0D0]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3 h-3 text-[#40E0D0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h4 className="text-[14px] font-semibold text-[#F5F5F5] mb-2">
                Collect Only (Locked)
              </h4>
              <p className="text-[12px] text-[#A0A0A0] leading-relaxed">
                Observer users can only save this song to their library. This promotes gradual
                engagement without overwhelming new users with action prompts.
              </p>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-[#282828]">
            <p className="text-[11px] font-medium text-[#808080] uppercase tracking-wider mb-2">
              Default Observer Copy (Preview)
            </p>
            <p className="text-[11px] text-[#606060] mb-3">
              This is how the modal appears to users at actual size
            </p>

            {/* Modal Preview at True Size */}
            <div className="flex justify-center py-4 px-4">
              <div
                className="bg-[#1A1A1A] rounded-xl border border-[#282828] w-full"
                style={{
                  maxWidth: '600px',
                  padding: '32px 24px',
                }}
              >
                <p className="text-[14px] text-[#A0A0A0] text-center mb-6 leading-relaxed">
                  You don't need to act yet. This is simply a moment to pause.
                </p>
                <h3 className="text-[16px] font-semibold text-[#40E0D0] text-center mb-3">
                  Some stories don't change what we think. They change what we notice.
                </h3>
                <p className="text-[14px] text-[#E0E0E0] text-center leading-[1.65] mb-4">
                  Meaning often shows up later. A song, a lyric, a feeling you can't shake.
                  You don't have to name it. It's enough to let it stay with you.
                </p>

                {/* Collect Button Preview */}
                <div className="pt-4 flex flex-col items-center">
                  <div
                    className="w-full max-w-[420px] flex items-center justify-center gap-2.5 px-8 rounded-full border"
                    style={{
                      height: '48px',
                      backgroundColor: 'transparent',
                      borderColor: 'rgba(64, 224, 208, 0.40)',
                    }}
                  >
                    <Layers
                      className="w-[18px] h-[18px]"
                      stroke="#40E0D0"
                      strokeWidth={1.5}
                      fill="none"
                    />
                    <span className="text-[15px] font-medium" style={{ color: '#40E0D0' }}>
                      Collect
                    </span>
                  </div>
                  <p className="text-center mt-1.5 w-full max-w-[420px]" style={{ fontSize: '12px', color: '#808080' }}>
                    Keep this with your other stories
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4">
        <p className="text-[12px] text-[#808080] leading-relaxed">
          Configure what action users can take after engaging with this song. Choose to collect
          only (save to library) or prompt a specific action.
        </p>
      </div>

      {/* Action Type Selection */}
      <div>
        <label className="block text-[13px] font-medium text-[#A0A0A0] mb-3">Action Type</label>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-4 bg-[#0A0A0A] border border-[#282828] rounded-lg cursor-pointer hover:border-[#404040] transition-colors">
            <input
              type="radio"
              checked={content.actionType === 'collect_only'}
              onChange={() => onChange({ actionType: 'collect_only' })}
              disabled={disabled}
              className="mt-0.5"
            />
            <div>
              <p className="text-[13px] font-medium text-[#F5F5F5]">Collect Only</p>
              <p className="text-[12px] text-[#808080] mt-1">
                Users can only save this song to their library. No additional action prompt.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 bg-[#0A0A0A] border border-[#282828] rounded-lg cursor-pointer hover:border-[#404040] transition-colors">
            <input
              type="radio"
              checked={content.actionType === 'micro_action'}
              onChange={() => onChange({ actionType: 'micro_action', microActionId: undefined })}
              disabled={disabled}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="text-[13px] font-medium text-[#F5F5F5]">Micro-Action</p>
              <p className="text-[12px] text-[#808080] mt-1 mb-3">
                Prompt users to complete a pre-defined civic micro-action.
              </p>
              {content.actionType === 'micro_action' && !disabled && (
                <div className="bg-[#FFB86C]/10 border border-[#FFB86C]/30 rounded-lg px-3 py-2">
                  <p className="text-[11px] text-[#FFB86C]">
                    Micro-action selection will be available in a future update. For now, use
                    custom text below.
                  </p>
                </div>
              )}
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 bg-[#0A0A0A] border border-[#282828] rounded-lg cursor-pointer hover:border-[#404040] transition-colors">
            <input
              type="radio"
              checked={content.actionType === 'custom'}
              onChange={() => onChange({ actionType: 'custom', customText: content.customText || '' })}
              disabled={disabled}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="text-[13px] font-medium text-[#F5F5F5]">Custom Action Text</p>
              <p className="text-[12px] text-[#808080] mt-1">
                Write custom text to prompt users to take action related to this song.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Custom Text Field */}
      {content.actionType === 'custom' && (
        <div>
          <label className="block text-[13px] font-medium text-[#A0A0A0] mb-3">
            Custom Action Text
          </label>
          <textarea
            value={content.customText || ''}
            onChange={(e) => onChange({ ...content, customText: e.target.value })}
            disabled={disabled}
            rows={4}
            placeholder="Write the action prompt or CTA text that will appear to users..."
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
          />
        </div>
      )}
    </div>
  );
}

export type { TakeItFurtherContent };
